import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useLocation, useNavigate } from "react-router-dom";

// Third-party packages.
import TableCSVExport from '../../../component/TableCSVExport';
import { Column, usePagination, useSortBy, useTable } from 'react-table';

// Constants.
import { DATA_EMPTY, DATA_FAILED, DATA_LOADING } from "../../../component/Constants";

// Components.
import AlertRules from "../../../types/Alerts/AlertRules";
import HostTarget from "../../../types/host/HostTarget";
import InstanceTarget from "../../../types/instance/InstanceTarget";
import ReactTable from "../../../component/ReactTable";
import { getDefaultPageSize, sortStrings, stringFormatting } from "../../../helpers/utils";
import ConditionalRender from "../../../helpers/ConditionalRender";
import SearchInput from '../../../component/SearchInput';

const SCOPE_PREVIEW_LIMIT = 2;

const summariseNames = (names: string[], label: string, total?: number): string => {
    if (names.length === 0) return `All ${label}`;
    if (total !== undefined && names.length === total) return `All ${label} (${total})`;
    if (names.length > SCOPE_PREVIEW_LIMIT) {
        const head = names.slice(0, SCOPE_PREVIEW_LIMIT).join(', ');
        return `${head} +${names.length - SCOPE_PREVIEW_LIMIT} more (${names.length} total)`;
    }
    return names.join(', ');
};

const formatScope = (rule: AlertRules, hosts: HostTarget[], instances: InstanceTarget[]): string => {
    if (rule.entitytype === 'host') {
        const names = (rule.host ?? []).map(id => hosts.find(h => h.id === id)?.name ?? `#${id}`);
        return summariseNames(names, 'Hosts', hosts.length);
    }
    if (rule.entitytype === 'disk') {
        const hostNames = (rule.host ?? []).map(id => hosts.find(h => h.id === id)?.name ?? `#${id}`);
        return `${summariseNames(hostNames, 'Hosts', hosts.length)} / ${summariseNames(rule.disk ?? [], 'Disks')}`;
    }
    if (rule.datasource && rule.datasource.length > 0) {
        const names = rule.datasource.map(id => instances.find(i => i.id === id)?.name ?? `#${id}`);
        return summariseNames(names, 'Instances', instances.length);
    }
    if (rule.tag && rule.tag.length > 0) {
        return rule.tag.map(t => `${t.name}=${t.value}`).join(', ');
    }
    return 'All Instances';
};

const formatThresholds = (rule: AlertRules): string => {
    // Status rules carry a backend placeholder threshold (e.g. 200) that has no meaning to users.
    if (rule.metrictype === 'status') return '';
    if (!rule.thresholdarray || rule.thresholdarray.length === 0) return '';
    const op = rule.decreasing ? '<=' : '>=';
    const unit = rule.units === '%' ? '%' : rule.units === 'ms' ? 'ms' : '';
    const sevLabel: Record<number, string> = { 1: 'Critical', 2: 'Warning', 3: 'Info' };
    return rule.thresholdarray
        .slice()
        .sort((a, b) => a.threshold - b.threshold)
        .map(t => `${sevLabel[t.severity] ?? `S${t.severity}`} ${op} ${t.threshold}${unit}`)
        .join(', ');
};

function List(props: { loading: number, refresh: Function, alertRules: AlertRules[], isAdmin: boolean, hosts: HostTarget[], instances: InstanceTarget[] }) {
    const navigate = useNavigate();
    const location = useLocation();
    const query = new URLSearchParams(location.search);
    const [tableFilter, setTableFilter] = useState(query.get("filter") || "");
    const initialPageIndex = Number(query.get("page") || 0);
    const initialPageSize = Number(query.get("pageSize") || getDefaultPageSize());

    function refresh() {
        props.refresh();
    }

    function clearTableFilter() {
        // Clear any pre-existing filter values.
        setTableFilter('');
    }

    const data: AlertRules[] = useMemo(() => props.alertRules.filter(alertRule => {
        const filter = tableFilter.toLowerCase();
        const scope = formatScope(alertRule, props.hosts, props.instances).toLowerCase();
        return (
            (alertRule.name ?? '').toLowerCase().includes(filter) ||
            (alertRule.entitytype ?? '').toLowerCase().includes(filter) ||
            (alertRule.statistic ?? '').toLowerCase().includes(filter) ||
            scope.includes(filter) ||
            (alertRule.metrictype ?? '').toLowerCase().includes(filter)
        )
    }), [props.alertRules, tableFilter, props.hosts, props.instances]);

    const columns: Column[] = useMemo(() => [
        {
            accessor: 'id',
            defaultCanSort: false,
        },
        {
            Header: 'Rule',
            accessor: 'name',
            className: 'fw-bold',
            headerClassName: 'col-width-20',
            defaultCanSort: true,
            sortType: sortStrings
        },
        {
            Header: 'Scope',
            id: 'scope',
            accessor: (row: any) => formatScope(row, props.hosts, props.instances),
            headerClassName: 'col-width-25',
            defaultCanSort: true,
            Cell: ({ cell }: any) => {
                const rule = cell.row.original as AlertRules;
                const hostNames = (rule.host ?? []).map(id => props.hosts.find(h => h.id === id)?.name ?? `#${id}`);
                const fullNames: string[] = rule.entitytype === 'host'
                    ? hostNames
                    : rule.entitytype === 'disk'
                    ? [...hostNames, ...(rule.disk ?? [])]
                    : (rule.datasource ?? []).map(id => props.instances.find(i => i.id === id)?.name ?? `#${id}`);
                const tooltip = fullNames.length > 0 ? fullNames.join(', ') : undefined;
                return (
                    <span title={tooltip}>{cell.value}</span>
                );
            }
        },
        {
            Header: 'Type',
            id: 'entitytype',
            accessor: (row: any) => `${row.entitytype === 'datasource' ? 'instance' : row.entitytype} ${row.metrictype}`,
            headerClassName: 'col-width-10',
            defaultCanSort: true,
            sortType: sortStrings,
            Cell: ({ cell }: any) => (
                <React.Fragment>
                    <span className='text-capitalize'>{cell.value}</span>
                </React.Fragment>
            )
        },
        {
            Header: 'Metric',
            id: 'statistic',
            accessor: (row: any) => stringFormatting(row.statistic ?? ''),
            headerClassName: 'col-width-15',
            defaultCanSort: true,
            sortType: sortStrings,
            className: 'text-capitalize',
            Cell: ({ cell }: any) => (
                <React.Fragment>
                    {cell.value}
                </React.Fragment>
            )
        },
        {
            Header: 'Thresholds',
            id: 'thresholds',
            accessor: (row: any) => formatThresholds(row),
            headerClassName: 'col-width-20',
            defaultCanSort: false,
            Cell: ({ cell }: any) => (
                <React.Fragment>
                    <span>{cell.value}</span>
                </React.Fragment>
            )
        },
        {
            Header: 'Window (m)',
            accessor: 'evaluationwindowminutes',
            headerClassName: 'col-width-5',
            defaultCanSort: true,
        },
        {
            Header: 'Enabled',
            accessor: 'disabled',
            headerClassName: 'col-width-5',
            disableSortBy: true,
            Cell: ({ cell }) => (
                <React.Fragment>
                    <div className='form-check form-switch'>
                        <input checked={!cell.row.values.disabled}
                               type="checkbox"
                               id="keepdata"
                               className="form-check-input"
                               disabled={true}
                               data-lpignore={true}
                        />
                    </div>
                </React.Fragment>
            )
        },
        ...(props.isAdmin ? [{
            Header: 'Actions',
            accessor: 'actions',
            className: 'actions',
            disableSortBy: true,
            headerClassName: 'actions',
            Cell: ({ cell }: any) => (
                <React.Fragment>
                    <Link to={`/admin/alert-rules/edit/${cell.row.values.id}`} role="button"
                          className="btn btn-sm btn-primary">
                        <i className="fal fa-edit fa-fw"/><span>Edit</span>
                    </Link>
                    <Link to={`/admin/alert-rules/delete/${cell.row.values.id}`} role="button"
                          className="btn btn-sm btn-danger">
                        <i className="fal fa-trash-alt fa-fw"/><span>Delete</span>
                    </Link>
                </React.Fragment>
            )
        }] : [])
    ], [props.isAdmin, props.hosts, props.instances]);

    // CSV Data transformation function
    const transformedData = useMemo(() => (csvData: any[]) => {
        return csvData.map((item: any) => {
            const originalRow = data.find(row => row.name === item.name);
            if (!originalRow) return item;

            return {
                ...item,
                scope: formatScope(originalRow, props.hosts, props.instances),
                entitytype: `${originalRow.entitytype === 'datasource' ? 'instance' : originalRow.entitytype} ${originalRow.metrictype}`,
                statistic: stringFormatting(originalRow.statistic),
                thresholds: formatThresholds(originalRow),
                disabled: !originalRow.disabled ? 'Yes' : 'No',
            };
        });
    }, [data, props.hosts, props.instances]);

    const tableInstance = useTable({
            columns,
            data,
            initialState: {
                hiddenColumns: ['id'],
                pageIndex: initialPageIndex,
                pageSize: initialPageSize,
                sortBy: [{ id: 'name', desc: false }]
            },
            autoResetPage: false,
            autoResetSortBy: false,
            autoResetFilters: false,
            autoResetSelectedRows: false,
            autoResetExpanded: false,
            autoResetGroupBy: false,
        },
        useSortBy, usePagination);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: { pageIndex, pageSize }
    } = tableInstance;

    useEffect(() => {
        const params = new URLSearchParams(location.search);

        if (tableFilter) {
            params.set('filter', tableFilter);
        } else {
            params.delete('filter');
        }

        params.set('page', pageIndex.toString());
        params.set('pageSize', pageSize.toString());

        navigate({ search: params.toString() }, { replace: true });
    }, [tableFilter, pageIndex, pageSize]);

    useEffect(() => {
        if (tableFilter) {
            gotoPage(0);
        }
    }, [tableFilter]);

    return (
        <div className="col h-100">
            <div className="card">
                <div className="card-header">
                    <i className="fal fa-exclamation-triangle fa-fw mr-10" aria-hidden="true"/>
                    Alert Rules
                    <span className="badge bg-info" data-tooltip-id="global-tooltip" data-tooltip-content="Total alert rules count">{data.length}</span>
                    <i className="collapse-toggle" role="button" data-bs-toggle="collapse"
                       data-bs-target="#collapseAlerts" aria-expanded="false" aria-controls="collapseAlerts"/>
                </div>
                <div id="collapseAlerts" className="card-body collapse show">
                    {props.loading === DATA_EMPTY ? (
                        <div className="w-100 text-center text-muted my-3">
                            <i className="fal fa-ban fa-fw fa-2x"/>
                            <p>
                                <button type="button" className="btn btn-link" onClick={refresh}>No Alert Rules Found -
                                    Click to Refresh
                                </button>
                            </p>
                        </div>
                    ) : (
                        props.loading === DATA_FAILED ? (
                            <div className="w-100 text-center text-muted my-3">
                                <i className="fal fa-ban fa-fw fa-2x text-danger"/>
                                <p>
                                    <button type="button" className="btn btn-link" onClick={refresh}>Failed to Retrieve
                                        Alert Rules - Click to Refresh
                                    </button>
                                </p>
                            </div>
                        ) : (
                            <React.Fragment>
                                <div className="row row-cols-1 row-cols-md-2 table-search">
<div className="col col-md-10">
                            <ConditionalRender if={props.isAdmin}>
                                            <Link to="/admin/alert-rules/create" role="button"
                                                  className="btn btn-sm btn-success">
                                                <i className="fal fa-plus-square fa-fw"/><span>Create</span>
                                            </Link>
                                        </ConditionalRender>
                                        <TableCSVExport
                                            tableInstance={tableInstance}
                                            filename="Alert Rules"
                                            transformData={transformedData}
                                        />
                                    </div>
                                    <div className="col col-md-2">
                                        <SearchInput
                                            value={tableFilter}
                                            onChange={(e) => setTableFilter(e.target.value)}
                                            onClear={clearTableFilter}
                                            autoFocus
                                        />
                                    </div>
                                </div>
                                <ReactTable
                                    getTableProps={getTableProps}
                                    getTableBodyProps={getTableBodyProps}
                                    headerGroups={headerGroups}
                                    prepareRow={prepareRow}
                                    page={page}
                                    canPreviousPage={canPreviousPage}
                                    canNextPage={canNextPage}
                                    pageOptions={pageOptions}
                                    pageCount={pageCount}
                                    gotoPage={gotoPage}
                                    nextPage={nextPage}
                                    previousPage={previousPage}
                                    setPageSize={setPageSize}
                                    pageIndex={pageIndex}
                                    pageSize={pageSize}
                                    dataLength={data.length}
                                    error={null}
                                    loading={props.loading === DATA_LOADING}
                                />
                            </React.Fragment>
                        )
                    )}
                </div>
            </div>
        </div>
    )
}

List.propTypes = {
    refresh: PropTypes.func.isRequired,
};

export default List;
